import { Modal, Button } from "antd";
import "./date-range-picker.css";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useMemo, useCallback, useContext, useEffect, useState } from "react";
import dayjs from "utils/dayjs-config";
import type { Dayjs } from "dayjs";
import { DatePicker } from "components/antd-date-picker";
import Separator from "./separator";

const { RangePicker } = DatePicker;

function DateRangePickerModal(props: any) {
  const {
    title,
    isOpen,
    setIsOpen,
    customDateRange,
    setCustomDateRange,
    onOkClick,
    hasActiveFilter,
    onClear,
    maxRangeDays
  } = props;
  const { calHolidaysWeekends }: TProjectContext = useContext(ProjectContext);
  const holidays = calHolidaysWeekends?.holidays;
  const weekends = calHolidaysWeekends?.weekends;

  // The calendar opens one render AFTER the modal, never on the same one.
  //
  // getPopupContainer hands rc-trigger the modal body (the picker's parent) to
  // portal the panel into, and rc-trigger resolves that container on the render
  // where `open` first turns true. On the very first render that parent is not
  // guaranteed, so the container comes back empty and the panel is silently
  // never created — a modal with a title, a footer and nothing in between.
  // Opening a tick later gives it a mounted node to portal into.
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  useEffect(() => setIsPanelOpen(isOpen), [isOpen]);

  // See custom-date-picker/index.tsx for the rationale: precomputing a
  // Set of project-zone day-keys once collapses the per-cell × per-
  // holiday loop to O(1). dayjs.tz(...) (vs the previous dayjs(...))
  // also fixes a latent off-by-one for clients east of the project
  // zone — CIQ-11176 fixed this for the single picker, this aligns the
  // range picker.
  const holidayDaySet = useMemo(() => {
    if (!holidays || holidays.length === 0) return null;
    return new Set(
      holidays
        .map((hld: any) =>
          hld?.date ? dayjs.tz(hld.date).format("YYYY-MM-DD") : null
        )
        .filter((s): s is string => !!s)
    );
  }, [holidays]);

  const weekendSet = useMemo(() => {
    if (!weekends || weekends.length === 0) return null;
    return new Set<number>(weekends);
  }, [weekends]);

  // `info.from` is the bound already picked, so the span can only be measured
  // while the second date is being chosen.
  const applyHolidays = useCallback(
    (current: Dayjs, info?: { from?: Dayjs }) => {
      if (holidayDaySet?.has(current.format("YYYY-MM-DD"))) return true;
      if (weekendSet?.has(current.day())) return true;
      if (maxRangeDays && info?.from) {
        if (Math.abs(current.diff(info.from, "day")) > maxRangeDays)
          return true;
      }
      return false;
    },
    [holidayDaySet, weekendSet, maxRangeDays]
  );

  return (
    <div>
      <Modal
        title={title}
        open={isOpen}
        className="custom-date-picker ag-custom-component-popup"
        rootClassName="ag-custom-component-popup"
        onCancel={() => {
          onOkClick();
          setIsOpen(false);
        }}
        data-testid="daterangepicker-modal"
        footer={
          <div className="w-full flex justify-end space-x-2">
            <Button
              key="back"
              className={
                hasActiveFilter ? "text-ciq-red-700 border-ciq-red-700" : ""
              }
              onClick={() => {
                if (hasActiveFilter) {
                  onClear();
                }
                setCustomDateRange();
                setIsOpen(false);
              }}
              data-testid="daterangepicker-button-cancel"
            >
              {hasActiveFilter ? "Clear" : "Cancel"}
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                onOkClick();
                setIsOpen(false);
              }}
              data-testid="daterangepicker-button-apply"
            >
              Apply
            </Button>
          </div>
        }
      >
        <RangePicker
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          open={isPanelOpen}
          needConfirm={false}
          className="customRangePicker1"
          format="DD-MM-YYYY"
          separator={Separator()}
          value={customDateRange}
          onCalendarChange={(dates) => setCustomDateRange(dates)}
          onChange={(value) => setCustomDateRange(value)}
          onOpenChange={(nextOpen) => {
            if (isOpen) setIsPanelOpen(true);
            else setIsPanelOpen(nextOpen);
          }}
          // Portal into the modal body, not the picker itself. rc-picker treats
          // clicks inside the trigger as "refocus start input", which reset the
          // in-progress range on every cell click after the antd 6 upgrade.
          getPopupContainer={(trigger) => trigger.parentElement ?? trigger}
          disabledDate={applyHolidays}
          data-testid="daterangepicker-button"
        />
      </Modal>
    </div>
  );
}

export default DateRangePickerModal;
